import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, FormControl, InputLabel } from '@mui/material';
import { DateTime } from 'luxon';

interface BaseProps {
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    minDate?: DateTime;
    maxDate?: DateTime;
    shouldDisableDate?: (day: DateTime) => boolean;
    renderMultipleValue?: (dates: string[]) => string;
    className?: string;
    sx?: any;
    hasError?: boolean;
}

interface SingleProps extends BaseProps {
    multiple?: false;
    value: string | null;
    onChange: (date: string) => void;
}

interface MultipleProps extends BaseProps {
    multiple: true;
    value: string[];
    onChange: (dates: string[]) => void;
}

export type DateSelectorProps = SingleProps | MultipleProps;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const FoldIcon = () => (
    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 6L5 2L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path
            d="M1 10L5 14L9 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const DateSelector: React.FC<DateSelectorProps> = (props) => {
    const {
        label,
        placeholder = 'select date',
        disabled = false,
        minDate,
        maxDate,
        shouldDisableDate,
        renderMultipleValue,
        className = '',
        sx = {},
        hasError = false
    } = props;

    const selectedDates = useMemo<string[]>(() => {
        const raw = props.multiple ? props.value : props.value ? [props.value] : [];
        return raw
            .map((d) => DateTime.fromISO(d))
            .filter((dt) => dt.isValid)
            .map((dt) => dt.toISODate()!);
    }, [props.multiple, props.value]);

    const [isOpen, setIsOpen] = useState(false);
    const [viewDate, setViewDate] = useState<DateTime>(() =>
        selectedDates[0] ? DateTime.fromISO(selectedDates[0]).startOf('month') : DateTime.local().startOf('month')
    );

    const containerRef = useRef<HTMLDivElement>(null);

    // Sync viewDate when value changes externally
    useEffect(() => {
        if (props.multiple || !selectedDates[0]) return;
        setViewDate(DateTime.fromISO(selectedDates[0]).startOf('month'));
    }, [props.multiple, selectedDates]);

    const handleClose = useCallback(() => setIsOpen(false), []);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                handleClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, handleClose]);

    const handleOpen = () => {
        if (disabled) return;
        setIsOpen(true);
    };

    const handlePrevMonth = (e: React.MouseEvent) => {
        e.stopPropagation();
        setViewDate((d) => d.minus({ months: 1 }));
    };

    const handleNextMonth = (e: React.MouseEvent) => {
        e.stopPropagation();
        setViewDate((d) => d.plus({ months: 1 }));
    };

    const handleSelectDate = (day: DateTime) => {
        const iso = day.toISODate();
        if (!iso) return;

        if (props.multiple) {
            const next = selectedDates.includes(iso)
                ? selectedDates.filter((d) => d !== iso)
                : [...selectedDates, iso].sort();
            props.onChange(next);
            return;
        }

        props.onChange(iso);
        handleClose();
    };

    const isDisabledDate = useCallback(
        (day: DateTime): boolean => {
            if (minDate && day < minDate.startOf('day')) return true;
            if (maxDate && day > maxDate.startOf('day')) return true;
            if (shouldDisableDate && shouldDisableDate(day)) return true;
            return false;
        },
        [minDate, maxDate, shouldDisableDate]
    );

    // Build calendar grid: always 5 rows x 7 cols starting Monday
    const buildCalendarDays = (): DateTime[] => {
        const firstDay = viewDate.startOf('month');
        const startOffset = (firstDay.weekday - 1 + 7) % 7;
        const gridStart = firstDay.minus({ days: startOffset });
        const totalDays = startOffset + viewDate.daysInMonth!;
        const gridSize = Math.ceil(totalDays / 7) * 7;
        const days: DateTime[] = [];
        for (let i = 0; i < gridSize; i++) {
            days.push(gridStart.plus({ days: i }));
        }
        return days;
    };

    const calendarDays = buildCalendarDays();
    const displayValue = props.multiple
        ? selectedDates.length
            ? (renderMultipleValue?.(selectedDates) ?? selectedDates.join(', '))
            : null
        : (selectedDates[0] ?? null);
    const isMenuVisible = isOpen;

    const outerWrapperStyle = disabled ? { cursor: 'not-allowed' } : {};
    const innerWrapperStyle = disabled ? { pointerEvents: 'none' as const } : {};

    return (
        <Box
            className={`selector-wrapper date-selector-wrapper ${className}`}
            ref={containerRef}
            sx={{ position: 'relative', ...sx }}
            style={outerWrapperStyle}
        >
            <FormControl
                fullWidth
                disabled={disabled}
                className={`selector-container ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
            >
                {label && (
                    <InputLabel shrink className="selector-label">
                        {label}
                    </InputLabel>
                )}

                <Box className="selector-input-wrapper" style={innerWrapperStyle}>
                    {/* Closed state input box */}
                    <Box
                        className={`selector-input-box ${hasError ? 'error' : ''}`}
                        onClick={disabled ? undefined : handleOpen}
                    >
                        {displayValue ? (
                            <span className={`selector-selected-value${disabled ? ' selector-disabled-value' : ''}`}>
                                {displayValue}
                            </span>
                        ) : (
                            <span className="selector-placeholder">{placeholder}</span>
                        )}
                        <span className="selector-icon">
                            <FoldIcon />
                        </span>
                    </Box>

                    {/* Open state dropdown */}
                    {isMenuVisible && (
                        <Box className={`date-selector-menu`}>
                            {/* Input box replica at top of menu */}
                            <Box className="selector-input-box date-selector-input-top">
                                {displayValue ? (
                                    <span className="selector-selected-value">{displayValue}</span>
                                ) : (
                                    <span className="selector-placeholder">{placeholder}</span>
                                )}
                                <span className="selector-icon" onClick={handleClose} style={{ cursor: 'pointer' }}>
                                    <FoldIcon />
                                </span>
                            </Box>

                            {/* Month/Year navigation */}
                            <Box className="date-selector-nav">
                                <button className="date-selector-nav-btn" onClick={handlePrevMonth} type="button">
                                    &#8249;
                                </button>
                                <span className="date-selector-month-label">{viewDate.toFormat('MMMM yyyy')}</span>
                                <button className="date-selector-nav-btn" onClick={handleNextMonth} type="button">
                                    &#8250;
                                </button>
                            </Box>

                            {/* Calendar grid */}
                            <Box className="date-selector-grid">
                                {WEEKDAYS.map((wd) => (
                                    <span key={wd} className="date-selector-weekday">
                                        {wd}
                                    </span>
                                ))}

                                {calendarDays.map((day, idx) => {
                                    const isCurrentMonth = day.month === viewDate.month;
                                    const isSelected = selectedDates.includes(day.toISODate() ?? '');
                                    const isToday = day.hasSame(DateTime.local(), 'day');
                                    const isDisabled = isDisabledDate(day);

                                    let cellClass = 'date-selector-day';
                                    if (!isCurrentMonth) cellClass += ' date-selector-day--other-month';
                                    if (isSelected) cellClass += ' date-selector-day--selected';
                                    if (isToday && !isSelected) cellClass += ' date-selector-day--today';
                                    if (isDisabled) cellClass += ' date-selector-day--disabled';

                                    return (
                                        <span
                                            key={idx}
                                            className={cellClass}
                                            onClick={() => !isDisabled && handleSelectDate(day)}
                                        >
                                            <span className="date-selector-day-number">{day.day}</span>
                                        </span>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </Box>
            </FormControl>
        </Box>
    );
};

export default DateSelector;
